import React, { useState } from 'react';
import { View, Pressable, StyleSheet, Text, TextInput, ScrollView } from 'react-native';
import { useTranslation } from 'react-i18next';
import { usePantryName } from './usePantryName';
import { PantryIcon } from './PantryIcon';
import { FoodTile } from './FoodTile';
import { useRecentFoods } from './useRecentFoods';
import { Paprika, PaprikaZones } from '../../styles/theme';
import { PantryLocation } from '../../utils/pantryLocation';

interface Props {
  zone: PantryLocation;
  onAdd: (food: { name: string; category: string }) => void;
  onPhoto: (zone: PantryLocation) => void;
  showPhotoButton?: boolean;
  showSearchField?: boolean;
  showRecentsLabel?: boolean;
  containerStyle?: any;
}

export function AddFoodContent({
  zone,
  onAdd,
  onPhoto,
  showPhotoButton = true,
  showSearchField = true,
  showRecentsLabel = true,
  containerStyle,
}: Props) {
  const { t } = useTranslation();
  const pantryName = usePantryName();
  const [search, setSearch] = useState('');
  const recents = useRecentFoods();
  const zoneColors = PaprikaZones[zone];

  // Filter recents by search
  const filtered = recents.filter((r) =>
    r.name.toLowerCase().includes(search.toLowerCase())
  );

  // Check if search matches any recent item
  const searchMatchesRecent = filtered.some(
    (r) => r.name.toLowerCase() === search.toLowerCase()
  );

  // Determine if we should show manual add chip
  const showManualAdd = search.trim().length > 0 && !searchMatchesRecent;

  const handleRecentAdd = (food: { name: string; category: string }) => {
    onAdd(food);
  };

  const handleManualAdd = () => {
    if (search.trim()) {
      onAdd({
        name: search.trim(),
        category: 'Other', // Will be detected by detectCategoryFromName
      });
    }
  };

  const handlePhoto = () => {
    onPhoto(zone);
  };

  return (
    <View style={containerStyle}>
      {/* Photo row */}
      {showPhotoButton && (
        <Pressable
          style={[
            styles.photoButton,
            {
              backgroundColor: zoneColors.tint,
              borderColor: `${zoneColors.accent}33`,
            },
          ]}
          onPress={handlePhoto}
        >
          <PantryIcon icon={{ lib: 'mci', name: 'camera' }} size={17} color={zoneColors.accent} />
          <Text style={[styles.photoLabel, { color: Paprika.textPrimary }]}>
            {t('pantry.add.photoSpace')}
          </Text>
          <View style={{ marginLeft: 'auto' }}>
            <PantryIcon
              icon={{ lib: 'mci', name: 'chevron-right' }}
              size={16}
              color={zoneColors.accent}
            />
          </View>
        </Pressable>
      )}

      {/* Search field */}
      {showSearchField && (
        <TextInput
          style={styles.searchInput}
          placeholder={t('pantry.add.searchPlaceholder')}
          placeholderTextColor={Paprika.textMuted}
          value={search}
          onChangeText={setSearch}
        />
      )}

      {/* Recents label */}
      {showRecentsLabel && <Text style={styles.recentsLabel}>{t('pantry.add.recents')}</Text>}

      {/* Recents chips or manual add chip */}
      <View style={styles.chipsContainer}>
        {showManualAdd && (
          <Pressable
            style={[
              styles.chip,
              {
                backgroundColor: Paprika.surfaceLowest,
                borderColor: Paprika.hairline,
              },
            ]}
            onPress={handleManualAdd}
          >
            <View
              style={[
                styles.chipIcon,
                {
                  backgroundColor: Paprika.surfaceLowest,
                  borderColor: Paprika.hairline,
                },
              ]}
            >
              <PantryIcon
                icon={{ lib: 'mci', name: 'plus' }}
                size={13}
                color={Paprika.textSecondary}
              />
            </View>
            <Text style={styles.chipLabel}>{`${t('pantry.add.addManual')} « ${search.trim()} »`}</Text>
            <PantryIcon
              icon={{ lib: 'mci', name: 'plus' }}
              size={14}
              color={zoneColors.accent}
            />
          </Pressable>
        )}

        {filtered.map((food) => (
          <Pressable
            key={food.name}
            style={[
              styles.chip,
              {
                backgroundColor: Paprika.surfaceLowest,
                borderColor: Paprika.hairline,
              },
            ]}
            onPress={() => handleRecentAdd(food)}
          >
            <View
              style={[
                styles.chipIcon,
                {
                  backgroundColor: Paprika.surfaceLowest,
                  borderColor: Paprika.hairline,
                },
              ]}
            >
              <FoodTile category={food.category} name={food.name} size={22} radius={6} />
            </View>
            <Text style={styles.chipLabel}>{pantryName(food)}</Text>
            <PantryIcon
              icon={{ lib: 'mci', name: 'plus' }}
              size={14}
              color={zoneColors.accent}
            />
          </Pressable>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  searchInput: {
    fontFamily: 'Inter-Regular',
    fontSize: 13.5,
    color: Paprika.textPrimary,
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Paprika.hairline,
    paddingVertical: 11,
    paddingHorizontal: 13,
    marginBottom: 12,
  },
  photoButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginBottom: 16,
    paddingVertical: 12,
    paddingHorizontal: 13,
    borderRadius: 12,
    borderWidth: 1,
  },
  photoLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 13.5,
    fontWeight: '600',
  },
  recentsLabel: {
    fontFamily: 'Inter-SemiBold',
    fontSize: 10.5,
    fontWeight: '700',
    letterSpacing: 0.05,
    textTransform: 'uppercase',
    color: Paprika.textMuted,
    marginBottom: 9,
  },
  chipsContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 16,
  },
  chip: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 7,
    paddingVertical: 8,
    paddingHorizontal: 13,
    paddingLeft: 8,
    borderRadius: 999,
    borderWidth: 1,
  },
  chipIcon: {
    width: 22,
    height: 22,
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 0,
  },
  chipLabel: {
    fontFamily: 'Inter-Medium',
    fontSize: 13,
    fontWeight: '500',
    color: Paprika.textPrimary,
  },
});
